import { ICONS } from 'components/Icon';
import { difference, noop, toNumber, without } from 'lodash';
import { Dimensions, Linking, Platform } from 'react-native';
import {
  ChecklistItemResult,
  ChecklistItemResultEnum,
} from 'store/assessments/types';
import { ChecklistItem } from 'store/organisations/types';
import { format } from 'date-fns';
import { FileTypeGroupsEnum } from 'service/file/types';
import {
  API_DATE_FORMAT,
  ScreenSize,
  UI_DATE_FORMAT_LONG,
  UI_DATE_FORMAT_LONG_WITHOUT_MONTH,
  UI_DATE_FORMAT_LONG_WITHOUT_YEAR,
  UI_DATE_FORMAT_SHORT,
  UI_DATE_FORMAT_SHORT_WITHOUT_MONTH,
  UI_DATE_FORMAT_SHORT_WITHOUT_YEAR,
} from './constants';
import { theme } from './theme';
import { ISODateString } from 'types/types';

export const appStoreUrlApple =
  'https://apps.apple.com/gb/app/riskbase/id1533825779';
export const appStoreUrlGoogle =
  'https://play.google.com/store/apps/details?id=riskbase.client';

export const shouldRedirectWeb = (): string | false => {
  const oldHostname = 'client.riskbase.uk';
  const newHostname = 'app.riskbase.uk';
  if (Platform.OS === 'web' && window.location.hostname === oldHostname) {
    return window.location.href.replace(oldHostname, newHostname);
  }
  return false;
};

// export const formatNumber = (number: number): string => {
//   return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
// };

export const openUrl = (url: string) => {
  if (Platform.OS === 'web') {
    window.open(url, '_blank');
  } else {
    try {
      Linking.openURL(url);
    } catch (_) {
      noop();
    }
  }
};

export function urlWithQuery(
  url: string,
  params: Record<string, string | number | boolean | undefined>,
): string {
  const u = new URL(url);

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue; // skip unset
    u.searchParams.set(key, String(value));
  }

  return u.toString();
}

export const sanitizeFilename = (
  filename: string,
  {
    extension = 'File',
    fallback = 'ext',
  }: {
    // Fallback filename if string is empty after sanitation
    fallback?: string;
    // Provide the extension to improve the length limit
    extension?: string;
  } = {
    fallback: 'File',
    extension: 'ext',
  },
): string => {
  const sanitized = filename
    // Only allow characters listed
    .replace(/[^a-zA-Z0-9._-]+/g, ' ')
    // Remove all trailing periods and whitespace
    .replace(/[. ]+$/, '')
    // Enforce length limit
    .slice(0, 255 - (extension.length + 1));
  return sanitized.length ? sanitized : fallback;
};

export const randomKey = () => {
  let randomStr = 'RB';
  const keyLength = 24;
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz1234567890';
  const charactersLength = characters.length;
  for (let index = 0; index < keyLength; index += 1) {
    randomStr += characters.charAt(
      Math.floor(Math.random() * charactersLength),
    );
  }
  return randomStr;
};

export const GENERICKEY_FIELDREPEATABLELIST = ({
  groupId,
  type,
  where,
}: {
  groupId: string;
  type: 'searchString' | 'order';
  where: 'onasset' | 'inassessment' | 'inAssessmentAdding';
}): string => `GENERICKEY_FIELDREPEATABLELIST+${groupId}+${type}+${where}`;

export const GENERICKEY_FINDINGSEARCH = ({
  type,
  where,
}: {
  type: 'actions' | 'controls';
  where: 'setupSuggestions';
}): string => `GENERICKEY_FIELDREPEATABLELIST+${type}+${where}`;

export const dateToNumber = (date?: Date): number =>
  Math.floor((date?.getTime() || Date.now()) / 1000);

export const numberToDate = (ts: number): Date => new Date(ts * 1000);

export function isoDateStringToDate(date: ISODateString): Date;
export function isoDateStringToDate(date: null): null;
export function isoDateStringToDate(date: ISODateString | null): Date | null;
export function isoDateStringToDate(iso: ISODateString | null): Date | null {
  return iso === null ? null : new Date(iso);
}

export const dateToIsoString = (date: Date): ISODateString =>
  date.toISOString() as ISODateString;

export const dateStringToNumber = (dateString: string): number =>
  dateToNumber(new Date(dateString));

export const numberToDateString = (ts: number) =>
  format(numberToDate(ts), API_DATE_FORMAT);

export const urlAddQueries = (url: string, queries: string[]) =>
  url +
  (queries.length
    ? `${url.includes('?') ? '&' : '?'}${queries.join('&')}`
    : '');

export const getScreenSize = (dimensions?: {
  width: number;
  height: number;
}): {
  width: number;
  height: number;
  size: ScreenSize;
} => {
  let width = 0;
  let height = 0;

  if (dimensions) {
    width = dimensions.width;
    height = dimensions.height;
  } else {
    const newDimensions = Dimensions.get('window');
    width = newDimensions.width;
    height = newDimensions.height;
  }

  return {
    width,
    height,
    size:
      Platform.OS === 'web'
        ? ScreenSize.large
        : Math.min(width, height) < 500
          ? ScreenSize.small
          : ScreenSize.large,
  };
};

export const resizeToWidthHeight = ({
  width,
  height,
  outgoingWidth,
  outgoingHeight,
  allowScaleUp = true,
}: {
  width: number;
  height: number;
  outgoingWidth: number;
  outgoingHeight: number;
  allowScaleUp?: boolean;
}): { width: number; height: number } => {
  const ratio = allowScaleUp
    ? Math.min(outgoingWidth / width, outgoingHeight / height)
    : Math.min(outgoingWidth / width, outgoingHeight / height, 1);
  return { width: width * ratio, height: height * ratio };
};

export const rbAddReferencePrefix = (reference: string): string =>
  `RB-${reference}`;

export const arrayToggleInclude = <T = number | string>(array: T[], item: T) =>
  array.includes(item) ? without(array, item) : [...array, item];

export const stringRemoveNewLines = (value: string): string => {
  return value
    .replace(/(\r\n|\n|\r)/gm, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

export const objectHasOwnProperty = <X extends {}, Y extends PropertyKey>(
  obj: X,
  prop: Y,
): obj is X & Record<Y, unknown> =>
  Object.prototype.hasOwnProperty.call(obj, prop);

export function mapObjectValues<T extends Record<string, unknown>, R>(
  obj: T,
  fn: (value: T[keyof T], key: keyof T) => R,
): { [K in keyof T]: R } {
  const result = {} as { [K in keyof T]: R };
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      result[key] = fn(obj[key], key);
    }
  }
  return result;
}

export const arrayJoin = (
  array: (number | string)[],
  separator: string,
  lastSeparator: string,
): string => {
  const newArray = [...array];
  if (!newArray.length) {
    return '';
  }
  if (newArray.length === 1) {
    return `${newArray[0]}`;
  }
  const lastItem = lastSeparator ? newArray.pop() : undefined;
  return (
    newArray.join(separator) + (lastSeparator ? lastSeparator + lastItem : '')
  );
};

export const filterArrayEmpty = <TValue>(
  value: TValue | null | undefined,
): value is TValue => {
  if (
    value === null ||
    value === undefined ||
    (value as unknown as string) === ''
  ) {
    return false;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const testDummy: TValue = value;
  return true;
};

export const arrayUnique = <T extends number | string>(array: T[]): T[] =>
  Array.from(array.reduce<Set<T>>((set, e) => set.add(e), new Set()));

export const filterArrayNullUndefined = <TValue>(
  value: TValue | null | undefined,
): value is TValue => {
  if (value === null || value === undefined) {
    return false;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const testDummy: TValue = value;
  return true;
};

export const objectRemoveNull = (obj: Record<string, unknown>) => {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== null));
};

// export const objectRemoveUndefined = <TValue extends Record<string, unknown>>(
//   obj: TValue,
// ): Partial<Required<TValue>> =>
//   Object.fromEntries(
//     Object.entries(obj).filter(([, v]) => v !== undefined),
//   ) as Partial<Required<TValue>>;

export const getChecklistOutcome = (
  isEditable: boolean,
  checklistItems: ChecklistItem[],
  checklistItemResults: Record<string, ChecklistItemResult | null>,
  returnWithNoResults: boolean = false,
):
  | undefined
  | {
      result: ChecklistItemResultEnum;
      percentage: number;
      complete: number;
      total: number;
      icon: ICONS;
      color: string;
      color2: string;
      color3: string;
    } => {
  let checklistOutcome: ChecklistItemResultEnum | undefined;
  let countChecklistItemResult = 0;

  Object.values(checklistItemResults).forEach((checklistItemResult) => {
    const result = checklistItemResult?.result;
    if (result) {
      countChecklistItemResult += 1;
      if (
        checklistOutcome !== ChecklistItemResultEnum.cross &&
        (!checklistOutcome || result !== ChecklistItemResultEnum.dash)
      ) {
        checklistOutcome = result;
      }
    }
  });

  if (!checklistOutcome && !returnWithNoResults) {
    return undefined;
  }

  return {
    result: checklistOutcome || ChecklistItemResultEnum.dash,
    complete: countChecklistItemResult,
    total: checklistItems.length,
    percentage: isEditable
      ? Math.floor((countChecklistItemResult / checklistItems.length) * 100)
      : 100,
    icon:
      checklistOutcome === ChecklistItemResultEnum.cross
        ? ICONS.XmarkLight
        : checklistOutcome === ChecklistItemResultEnum.tick
          ? ICONS.CheckLight
          : ICONS.DashLight,
    color:
      checklistOutcome === ChecklistItemResultEnum.cross
        ? theme.colors.red
        : checklistOutcome === ChecklistItemResultEnum.tick
          ? theme.colors.green
          : theme.colors.black,
    color2:
      checklistOutcome === ChecklistItemResultEnum.cross
        ? theme.colors.red10
        : checklistOutcome === ChecklistItemResultEnum.tick
          ? theme.colors.green10
          : theme.colors.background,
    color3:
      checklistOutcome === ChecklistItemResultEnum.cross
        ? theme.colors.red20
        : checklistOutcome === ChecklistItemResultEnum.tick
          ? theme.colors.green20
          : '#999999',
  };
};

export const appendPunctuation = (string: string, character: string = '.') => {
  const returnString = string.trim();
  if (!returnString) {
    return returnString;
  }
  const lastCharacter = returnString[string.length - 1];
  if (!lastCharacter || ['.', '!', '?'].includes(lastCharacter)) {
    return returnString;
  }
  return returnString + character;
};

export const getAseResultKey = (
  id: string | number,
  fieldInstanceId?: string | null,
) => `${id}${fieldInstanceId ? `-fi:${fieldInstanceId}` : ''}`;

export const parseCurrency = (value: string): number => {
  const [rawPounds = '0', rawPence = '0'] = value
    .replace(/[^\d.]/g, '')
    .split('.');
  return (rawPounds ? +rawPounds * 100 : 0) + (rawPence ? +rawPence : 0);
};

export const dateQuarter = (date: Date) => {
  const quarter: 1 | 2 | 3 | 4 = Math.ceil((date.getMonth() + 1) / 3) as
    | 1
    | 2
    | 3
    | 4;
  const months = [
    ['January', 'February', 'March'],
    ['April', 'May', 'June'],
    ['July', 'August', 'September'],
    ['October', 'November', 'December'],
  ][quarter - 1]!;
  const monthsShort = [
    ['Jan', 'Feb', 'Mar'],
    ['Apr', 'May', 'Jun'],
    ['Jul', 'Aug', 'Sep'],
    ['Oct', 'Nov', 'Dec'],
  ][quarter - 1]!;
  return {
    quarter,
    months,
    monthsShort,
    startDate: new Date(date.getFullYear(), quarter * 3 - 3, 1, 0, 0, 0),
    endDate: new Date(date.getFullYear(), quarter * 3, 0, 23, 59, 59),
  };
};

export const dateWeek = (date: Date) => {
  const startDate = new Date(date.getTime());
  startDate.setDate(date.getDate() - [6, 0, 1, 2, 3, 4, 5][date.getDay()]!);
  startDate.setHours(0, 0, 0);
  const endDate = new Date(startDate.getTime());
  endDate.setDate(endDate.getDate() + 6);
  endDate.setHours(23, 59, 59);
  return {
    startDate,
    endDate,
  };
};

export const dateMonth = (date: Date) => {
  const startDate = new Date(date.getTime());
  startDate.setDate(1);
  startDate.setHours(0, 0, 0);
  const endDate = new Date(startDate.getTime());
  endDate.setMonth(endDate.getMonth() + 1);
  endDate.setDate(0);
  endDate.setHours(23, 59, 59);
  return {
    startDate,
    endDate,
  };
};

export const dateYear = (date: Date) => {
  const startDate = new Date(date.getTime());
  startDate.setMonth(0);
  startDate.setDate(1);
  startDate.setHours(0, 0, 0);
  const endDate = new Date(startDate.getTime());
  endDate.setMonth(11);
  endDate.setDate(31);
  endDate.setHours(23, 59, 59);
  return {
    startDate,
    endDate,
  };
};

export const formatDate = (date: Date | number, formatString: string): string =>
  format(typeof date === 'number' ? numberToDate(date) : date, formatString);

/**
 * Format a date range like:
 * 10 November 2026 – 2 January 2027 (different year)
 * 10 November – 12 December 2026 (different month, alwaysWithYear=true)
 * 10 November – 12 December (different month, alwaysWithYear=false)
 * 10 – 12 December 2026 (same month, alwaysWithYear=true)
 * 10 – 12 December (same month, alwaysWithYear=false)
 * 12 December 2026 (same day, alwaysWithYear=true)
 * 12 December (same day, alwaysWithYear=false)
 */
export const formatDateRange = (
  date1: Date | number,
  date2: Date | number,
  formatKey: 'short' | 'long',
  alwaysWithYear: boolean = true,
): string => {
  const finalDate1 = typeof date1 === 'number' ? numberToDate(date1) : date1;
  const finalDate2 = typeof date2 === 'number' ? numberToDate(date2) : date2;
  const yearMatch = finalDate1.getFullYear() === finalDate2.getFullYear();
  const monthMatch =
    yearMatch && finalDate1.getMonth() === finalDate2.getMonth();
  const dayMatch = monthMatch && finalDate1.getDate() === finalDate2.getDate();
  const dateFormat =
    formatKey === 'short'
      ? alwaysWithYear || !yearMatch
        ? UI_DATE_FORMAT_SHORT
        : UI_DATE_FORMAT_SHORT_WITHOUT_YEAR
      : alwaysWithYear || !yearMatch
        ? UI_DATE_FORMAT_LONG
        : UI_DATE_FORMAT_LONG_WITHOUT_YEAR;
  if (dayMatch) {
    return format(finalDate2, dateFormat);
  }
  const dateFormatWithoutYear =
    formatKey === 'short'
      ? UI_DATE_FORMAT_SHORT_WITHOUT_YEAR
      : UI_DATE_FORMAT_LONG_WITHOUT_YEAR;
  const dateFormatWithoutMonth =
    formatKey === 'short'
      ? UI_DATE_FORMAT_SHORT_WITHOUT_MONTH
      : UI_DATE_FORMAT_LONG_WITHOUT_MONTH;
  return `${format(
    finalDate1,
    monthMatch
      ? dateFormatWithoutMonth
      : yearMatch
        ? dateFormatWithoutYear
        : dateFormat,
  )} – ${format(finalDate2, dateFormat)}`;
};

export function getSearchTermsFromSearchValue(searchValue: string): string[] {
  return searchValue
    .toLowerCase()
    .replace(/[^A-Za-z0-9.\s-]/gi, ' ')
    .split(' ')
    .filter(filterArrayEmpty);
}

export const formatCurrency = (
  value: number,
  withSign: boolean = true,
  withCommas: boolean = true,
): string =>
  `${withSign ? (value < 0 ? '-£' : '£') : ''}` +
  `${
    withCommas
      ? (Math.abs(value) / 100)
          .toFixed(2)
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
      : (Math.abs(value) / 100).toFixed(2)
  }`;

type StandardEnum<T> = {
  [id: string]: T | string;
  [nu: number]: string;
};

// const validateEnum<T extends StandardEnum<unknown>> = (
//   enumType: T,
//   value: unknown,
//   ifInvalid?: undefined
// ): T[keyof T] | false;

// const validateEnum<T extends StandardEnum<unknown>> = (
//   enumType: T,
//   value: unknown,
//   ifInvalid: T[keyof T]
// ): T[keyof T];

export const validateEnumFallback = <
  T extends StandardEnum<unknown>,
  E extends T[keyof T],
  F extends T[keyof T],
>(
  enumType: T,
  value: unknown,
  ifInvalid: F,
): E | F => {
  if (Object.values(enumType).includes(value)) {
    return value as E;
  }
  return ifInvalid;
};

export const canPreviewFileGroup = (fileGroup: FileTypeGroupsEnum) => {
  if (
    fileGroup === FileTypeGroupsEnum.pdf ||
    fileGroup === FileTypeGroupsEnum.image
  ) {
    return true;
  }
  if (fileGroup === FileTypeGroupsEnum.svg && Platform.OS === 'web') {
    return true;
  }
  return false;
};

export const floorStringToNumber = [
  {
    label: 'Site',
    key: 'site',
    number: -50,
    showAsSuggestion: false,
  },
  { label: 'Environs', key: 'environs', number: -50, showAsSuggestion: true },
  { label: '', key: 'environ', number: -50, showAsSuggestion: false },
  {
    label: 'Orientation',
    key: 'orientation',
    number: -50,
    showAsSuggestion: false,
  },
  { label: 'Roof', key: 'roof', number: 998, showAsSuggestion: true },
  { label: 'Basement', key: 'basement', number: -2, showAsSuggestion: true },
  {
    label: 'Basement -10',
    key: 'basement8',
    number: -12,
    showAsSuggestion: false,
  },
  {
    label: 'Basement -9',
    key: 'basement8',
    number: -11,
    showAsSuggestion: false,
  },
  {
    label: 'Basement -8',
    key: 'basement8',
    number: -10,
    showAsSuggestion: false,
  },
  {
    label: 'Basement -7',
    key: 'basement7',
    number: -9,
    showAsSuggestion: false,
  },
  {
    label: 'Basement -6',
    key: 'basement6',
    number: -8,
    showAsSuggestion: false,
  },
  {
    label: 'Basement -5',
    key: 'basement5',
    number: -7,
    showAsSuggestion: false,
  },
  {
    label: 'Basement -4',
    key: 'basement4',
    number: -6,
    showAsSuggestion: false,
  },
  {
    label: 'Basement -3',
    key: 'basement3',
    number: -5,
    showAsSuggestion: true,
  },
  {
    label: 'Basement -2',
    key: 'basement2',
    number: -4,
    showAsSuggestion: true,
  },
  {
    label: 'Basement -1',
    key: 'basement1',
    number: -3,
    showAsSuggestion: true,
  },
  { label: 'Ground Floor', key: 'ground', number: 0, showAsSuggestion: true },
  { label: '', key: 'zero', number: 0, showAsSuggestion: false },
  {
    label: 'Lower Ground Floor',
    key: 'lowerground',
    number: -1,
    showAsSuggestion: true,
  },
  { label: 'First Floor', key: 'first', number: 1, showAsSuggestion: true },
  { label: '', key: 'one', number: 1, showAsSuggestion: false },
  { label: 'Second Floor', key: 'second', number: 2, showAsSuggestion: true },
  { label: '', key: 'two', number: 2, showAsSuggestion: false },
  { label: 'Third Floor', key: 'third', number: 3, showAsSuggestion: true },
  { label: '', key: 'three', number: 3, showAsSuggestion: false },
  { label: 'Fourth Floor', key: 'fourth', number: 4, showAsSuggestion: true },
  { label: '', key: 'four', number: 4, showAsSuggestion: false },
  { label: 'Fifth Floor', key: 'fifth', number: 5, showAsSuggestion: true },
  { label: '', key: 'five', number: 5, showAsSuggestion: false },
  { label: 'Sixth Floor', key: 'sixth', number: 6, showAsSuggestion: true },
  { label: '', key: 'six', number: 6, showAsSuggestion: false },
  { label: 'Seventh Floor', key: 'seventh', number: 7, showAsSuggestion: true },
  { label: '', key: 'seven', number: 7, showAsSuggestion: false },
  { label: 'Eighth Floor', key: 'eighth', number: 8, showAsSuggestion: true },
  { label: '', key: 'eight', number: 8, showAsSuggestion: false },
  { label: 'Ninth Floor', key: 'ninth', number: 9, showAsSuggestion: true },
  { label: '', key: 'nine', number: 9, showAsSuggestion: false },
  { label: 'Tenth Floor', key: 'tenth', number: 10, showAsSuggestion: true },
  { label: '', key: 'ten', number: 10, showAsSuggestion: false },
  {
    label: 'Eleventh Floor',
    key: 'eleventh',
    number: 11,
    showAsSuggestion: true,
  },
  { label: '', key: 'eleven', number: 11, showAsSuggestion: false },
  {
    label: 'Twelfth Floor',
    key: 'twelfth',
    number: 12,
    showAsSuggestion: true,
  },
  { label: '', key: 'twelve', number: 12, showAsSuggestion: false },
  {
    label: 'Thirteenth Floor',
    key: 'thirteenth',
    number: 13,
    showAsSuggestion: true,
  },
  { label: '', key: 'thirteen', number: 13, showAsSuggestion: false },
  {
    label: 'Fourteenth Floor',
    key: 'fourteenth',
    number: 14,
    showAsSuggestion: true,
  },
  { label: '', key: 'fourteen', number: 14, showAsSuggestion: false },
  {
    label: 'Fifteenth Floor',
    key: 'fifteenth',
    number: 15,
    showAsSuggestion: true,
  },
  { label: '', key: 'fifteen', number: 15, showAsSuggestion: false },
  {
    label: 'Sixteenth Floor',
    key: 'sixteenth',
    number: 16,
    showAsSuggestion: true,
  },
  { label: '', key: 'sixteen', number: 16, showAsSuggestion: false },
  {
    label: 'Seventeenth Floor',
    key: 'seventeenth',
    number: 17,
    showAsSuggestion: true,
  },
  { label: '', key: 'seventeen', number: 17, showAsSuggestion: false },
  {
    label: 'Eighteenth Floor',
    key: 'eighteenth',
    number: 18,
    showAsSuggestion: true,
  },
  { label: '', key: 'eighteen', number: 18, showAsSuggestion: false },
  {
    label: 'Nineteenth Floor',
    key: 'nineteenth',
    number: 19,
    showAsSuggestion: true,
  },
  { label: '', key: 'nineteen', number: 19, showAsSuggestion: false },
  {
    label: 'Twentieth Floor',
    key: 'twentieth',
    number: 20,
    showAsSuggestion: true,
  },
  { label: '', key: 'twenty', number: 20, showAsSuggestion: false },
  {
    label: 'Twenty-First Floor',
    key: 'twentyfirst',
    number: 21,
    showAsSuggestion: true,
  },
  { label: '', key: 'twentyone', number: 21, showAsSuggestion: false },
  {
    label: 'Twenty-Second Floor',
    key: 'twentysecond',
    number: 22,
    showAsSuggestion: true,
  },
  { label: '', key: 'twentytwo', number: 22, showAsSuggestion: false },
  {
    label: 'Twenty-Third Floor',
    key: 'twentythird',
    number: 23,
    showAsSuggestion: true,
  },
  { label: '', key: 'twentythree', number: 23, showAsSuggestion: false },
  {
    label: 'Twenty-Fourth Floor',
    key: 'twentyfourth',
    number: 24,
    showAsSuggestion: true,
  },
  { label: '', key: 'twentyfour', number: 24, showAsSuggestion: false },
  {
    label: 'Twenty-Fifth Floor',
    key: 'twentyfifth',
    number: 25,
    showAsSuggestion: true,
  },
  { label: '', key: 'twentyfive', number: 25, showAsSuggestion: false },
  {
    label: 'Twenty-Sixth Floor',
    key: 'twentysixth',
    number: 26,
    showAsSuggestion: true,
  },
  { label: '', key: 'twentysix', number: 26, showAsSuggestion: false },
  {
    label: 'Twenty-Seventh Floor',
    key: 'twentyseventh',
    number: 27,
    showAsSuggestion: true,
  },
  { label: '', key: 'twentyseven', number: 27, showAsSuggestion: false },
  {
    label: 'Twenty-Eighth Floor',
    key: 'twentyeighth',
    number: 28,
    showAsSuggestion: true,
  },
  { label: '', key: 'twentyeight', number: 28, showAsSuggestion: false },
  {
    label: 'Twenty-Ninth Floor',
    key: 'twentyninth',
    number: 29,
    showAsSuggestion: true,
  },
  { label: '', key: 'twentynine', number: 29, showAsSuggestion: false },
  {
    label: 'Thirtieth Floor',
    key: 'thirtieth',
    number: 30,
    showAsSuggestion: true,
  },
  { label: '', key: 'thirty', number: 30, showAsSuggestion: false },
  { label: '', key: 'thirtyfirst', number: 31, showAsSuggestion: false },
  { label: '', key: 'thirtyone', number: 31, showAsSuggestion: false },
  { label: '', key: 'thirtysecond', number: 32, showAsSuggestion: false },
  { label: '', key: 'thirtytwo', number: 32, showAsSuggestion: false },
  { label: '', key: 'thirtythird', number: 33, showAsSuggestion: false },
  { label: '', key: 'thirtythree', number: 33, showAsSuggestion: false },
  { label: '', key: 'thirtyfourth', number: 34, showAsSuggestion: false },
  { label: '', key: 'thirtyfour', number: 34, showAsSuggestion: false },
  { label: '', key: 'thirtyfifth', number: 35, showAsSuggestion: false },
  { label: '', key: 'thirtyfive', number: 35, showAsSuggestion: false },
  { label: '', key: 'thirtysixth', number: 36, showAsSuggestion: false },
  { label: '', key: 'thirtysix', number: 36, showAsSuggestion: false },
  { label: '', key: 'thirtyseventh', number: 37, showAsSuggestion: false },
  { label: '', key: 'thirtyseven', number: 37, showAsSuggestion: false },
  { label: '', key: 'thirtyeighth', number: 38, showAsSuggestion: false },
  { label: '', key: 'thirtyeight', number: 38, showAsSuggestion: false },
  { label: '', key: 'thirtyninth', number: 39, showAsSuggestion: false },
  { label: '', key: 'thirtynine', number: 39, showAsSuggestion: false },
  { label: '', key: 'fortieth', number: 40, showAsSuggestion: false },
  { label: '', key: 'forty', number: 40, showAsSuggestion: false },
  { label: '', key: 'fortyfirst', number: 41, showAsSuggestion: false },
  { label: '', key: 'fortyone', number: 41, showAsSuggestion: false },
  { label: '', key: 'fortysecond', number: 42, showAsSuggestion: false },
  { label: '', key: 'fortytwo', number: 42, showAsSuggestion: false },
  { label: '', key: 'fortythird', number: 43, showAsSuggestion: false },
  { label: '', key: 'fortythree', number: 43, showAsSuggestion: false },
  { label: '', key: 'fortyfourth', number: 44, showAsSuggestion: false },
  { label: '', key: 'fortyfour', number: 44, showAsSuggestion: false },
  { label: '', key: 'fortyfifth', number: 45, showAsSuggestion: false },
  { label: '', key: 'fortyfive', number: 45, showAsSuggestion: false },
  { label: '', key: 'fortysixth', number: 46, showAsSuggestion: false },
  { label: '', key: 'fortysix', number: 46, showAsSuggestion: false },
  { label: '', key: 'fortyseventh', number: 47, showAsSuggestion: false },
  { label: '', key: 'fortyseven', number: 47, showAsSuggestion: false },
  { label: '', key: 'fortyeighth', number: 48, showAsSuggestion: false },
  { label: '', key: 'fortyeight', number: 48, showAsSuggestion: false },
  { label: '', key: 'fortyninth', number: 49, showAsSuggestion: false },
  { label: '', key: 'fortynine', number: 49, showAsSuggestion: false },
  { label: '', key: 'fiftieth', number: 50, showAsSuggestion: false },
  { label: '', key: 'fifty', number: 50, showAsSuggestion: false },
  { label: '', key: 'fiftyfirst', number: 51, showAsSuggestion: false },
  { label: '', key: 'fiftyone', number: 51, showAsSuggestion: false },
  { label: '', key: 'fiftysecond', number: 52, showAsSuggestion: false },
  { label: '', key: 'fiftytwo', number: 52, showAsSuggestion: false },
  { label: '', key: 'fiftythird', number: 53, showAsSuggestion: false },
  { label: '', key: 'fiftythree', number: 53, showAsSuggestion: false },
  { label: '', key: 'fiftyfour', number: 54, showAsSuggestion: false },
  { label: '', key: 'fiftyfourth', number: 54, showAsSuggestion: false },
  { label: '', key: 'fiftyfifth', number: 55, showAsSuggestion: false },
  { label: '', key: 'fiftyfive', number: 55, showAsSuggestion: false },
  { label: '', key: 'fiftysixth', number: 56, showAsSuggestion: false },
  { label: '', key: 'fiftysix', number: 56, showAsSuggestion: false },
  { label: '', key: 'fiftyseventh', number: 57, showAsSuggestion: false },
  { label: '', key: 'fiftyseven', number: 57, showAsSuggestion: false },
  { label: '', key: 'fiftyeighth', number: 58, showAsSuggestion: false },
  { label: '', key: 'fiftyeight', number: 58, showAsSuggestion: false },
  { label: '', key: 'fiftynine', number: 59, showAsSuggestion: false },
  { label: '', key: 'fiftyninth', number: 59, showAsSuggestion: false },
  { label: 'Elevation', key: 'elevation', number: 999, showAsSuggestion: true },
].reverse();
const floorStringAfterWords = ['mezzanine'];
export const getPlanFloorOrder = (
  floorName: string,
  defaultPosition: number = -999,
) => {
  let addition = 0;
  const ogSplitName = floorName.toLowerCase().split(' ');
  const splitName = difference(ogSplitName, floorStringAfterWords);
  if (ogSplitName.length !== splitName.length) {
    addition += 10;
  }
  const modifiedName = splitName.join('').replaceAll('-', '');
  for (const floor of floorStringToNumber) {
    if (modifiedName.includes(floor.key)) {
      return floor.number * 100 + addition;
    }
  }
  const modifiedNumber = floorName.match(/-?[0-9]+/g);
  if (modifiedNumber?.length) {
    let finalNumber = toNumber(modifiedNumber[0]);
    if (finalNumber < 0) {
      finalNumber -= 2;
    }
    return finalNumber * 100 + addition;
  }
  return defaultPosition * 100 + addition;
};

export function safeAreaBottomIsRequired(isInModal: boolean): boolean {
  return (
    isInModal &&
    (Platform.OS === 'ios'
      ? !Platform.isPad
      : getScreenSize().size === ScreenSize.small)
  );
}

export function getPageHeaderPaddingTop(
  isInModal: boolean,
  additionalPadding: number,
): {
  paddingTopNormal: number;
  paddingTop: number;
} {
  const screenSize = getScreenSize();
  const paddingTopNormal = 16;
  const paddingTop =
    (isInModal || Platform.OS !== 'ios' || screenSize.size !== ScreenSize.small
      ? paddingTopNormal
      : 0) + additionalPadding;
  return { paddingTopNormal, paddingTop };
}
